/**
 * Interview State Manager
 * 
 * Single Responsibility: Manages interview question state tracking
 * Follows Single Source of Truth principle
 */

import { InterviewQuestion, InterviewState } from './types';

export class InterviewStateManager {
  private state: InterviewState = {
    currentQuestionIndex: 0,
    totalQuestions: 0,
    questions: [],
    answeredQuestions: [],
  };

  /**
   * Initialize state with questions
   */
  public initialize(questions: InterviewQuestion[]): void {
    this.state = {
      currentQuestionIndex: 0,
      totalQuestions: questions.length,
      questions: questions,
      answeredQuestions: [],
    };
  }

  /**
   * Get current state (deep clone to prevent mutation)
   */
  public getState(): InterviewState {
    if (typeof structuredClone !== 'undefined') {
      return structuredClone(this.state);
    }
    return JSON.parse(JSON.stringify(this.state));
  }

  /**
   * Get current question
   */
  public getCurrentQuestion(): InterviewQuestion | null {
    return this.state.questions[this.state.currentQuestionIndex] || null;
  }

  /**
   * Get next question to ask
   */
  public getNextQuestion(): InterviewQuestion | null {
    const nextIndex = this.state.currentQuestionIndex + 1;
    if (nextIndex >= this.state.questions.length) {
      return null;
    }
    return this.state.questions[nextIndex] || null;
  }

  /**
   * Mark current question as answered and advance to next
   */
  public markQuestionAnswered(questionId?: string): void {
    const currentQuestion = this.state.questions[this.state.currentQuestionIndex];
    if (!currentQuestion) return;

    const qId = questionId || currentQuestion.id;
    if (!this.state.answeredQuestions.includes(qId)) {
      this.state.answeredQuestions.push(qId);
    }

    // Clamp to valid range: max index is totalQuestions - 1 (0-based array)
    const maxIndex = Math.max(0, this.state.totalQuestions - 1);
    this.state.currentQuestionIndex = Math.min(
      this.state.currentQuestionIndex + 1,
      maxIndex
    );
  }

  /**
   * Check if all questions have been answered
   * Single Source of Truth for this calculation
   */
  public areAllQuestionsAnswered(): boolean {
    return (
      this.state.answeredQuestions.length >= this.state.totalQuestions &&
      this.state.totalQuestions > 0
    );
  }

  /**
   * Check if interview has questions
   */
  public hasQuestions(): boolean {
    return this.state.totalQuestions > 0;
  }

  /**
   * Check if we're past the introduction phase
   */
  public isPastIntroduction(): boolean {
    return this.state.currentQuestionIndex > 0;
  }
}

